import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Logo from './Logo';
import { useTranslation } from 'react-i18next';
import {
  Activity, BarChart3, Bell, BookOpen, Briefcase, Building2, FolderLock,
  Gauge, Gift, LayoutDashboard, LineChart, LogOut, MessageSquare, Settings,
  UserRound,
} from 'lucide-react';
import { getUnreadCount } from '@/utils/unread';

/* One icon set, drawn rather than photographed.
   The nav used to mix ten raster PNGs off the asset bucket with four lucide
   glyphs, so stroke weight and optical size changed line to line — and a
   bitmap cannot follow the active colour, so the accent stopped at the label
   while the icon beside it stayed black. Every entry is lucide now: one
   weight, and `currentColor` carries the accent through the whole row. */

type NavItem = {
  label: string;
  path?: string;
  icon: React.ElementType;
};

type SidebarProps = {
  onLogout?: () => void;
};

// Communications items are identical for both service views.
const navComms: NavItem[] = [
  { label: "sidebar.notifications", path: "/notifications", icon: Bell },
  { label: "sidebar.messageAdmin", path: "/message-admin", icon: MessageSquare },
];

// Determine which service view drives the sidebar. The Dashboard persists the
// user's active view to localStorage ('activeServiceView'); fall back to the
// signup/setup service when not set.
function getServiceMode(): 'accounting' | 'consulting' {
  const explicit = localStorage.getItem('activeServiceView');
  if (explicit === 'accounting' || explicit === 'consulting') return explicit;
  const ss = localStorage.getItem('setupService');
  return (ss === 'operate' || ss === 'incorporate') ? 'accounting' : 'consulting';
}

/** Group heading. Quieter than the rows it labels — it used to be bold black,
 *  which made the signpost louder than the destinations under it. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-gray-400">
      {children}
    </h3>
  );
}

function NavRow({ item, active, badge, onNavigate }: {
  item: NavItem;
  active: boolean;
  badge?: number;
  onNavigate: (path?: string) => void;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const disabled = !item.path;

  return (
    /* data-tour lets the onboarding tour spotlight a nav entry by path. */
    <li data-tour={`nav:${item.path}`}>
      <button
        onClick={() => onNavigate(item.path)}
        disabled={disabled}
        aria-current={active ? 'page' : undefined}
        className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start text-sm transition-colors ${active
          ? "acc-soft-bg acc-text font-semibold"
          : disabled
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 cursor-pointer"
          }`}
      >
        {/* Rail on the leading edge: the tint alone is faint against the warm
            page on corporate, so the current row keeps a hard marker. */}
        {active && (
          <span className="absolute start-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-e-full bg-current" />
        )}
        <Icon
          className={`h-[18px] w-[18px] shrink-0 transition-colors ${active ? "" : "text-gray-400 group-hover:text-gray-600"}`}
          strokeWidth={active ? 2.2 : 1.75}
        />
        <span className="truncate">{t(item.label)}</span>
        {badge ? (
          <span className="ms-auto grid h-[18px] min-w-[18px] place-items-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold leading-none text-white">
            {badge > 9 ? '9+' : badge}
          </span>
        ) : null}
      </button>
    </li>
  );
}

export default function Sidebar({ onLogout }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { t } = useTranslation();

  // Keep the sidebar in sync with the Dashboard's active service view.
  const [mode, setMode] = useState<'accounting' | 'consulting'>(getServiceMode);
  useEffect(() => {
    const update = () => setMode(getServiceMode());
    window.addEventListener('storage', update);
    window.addEventListener('serviceViewChanged', update);
    return () => {
      window.removeEventListener('storage', update);
      window.removeEventListener('serviceViewChanged', update);
    };
  }, []);
  const isAccounting = mode === 'accounting';

  // Unread notification count for the Notifications nav badge.
  const [unread, setUnread] = useState(getUnreadCount());
  useEffect(() => {
    const upd = () => setUnread(getUnreadCount());
    window.addEventListener('connct-unread', upd);
    window.addEventListener('storage', upd);
    return () => { window.removeEventListener('connct-unread', upd); window.removeEventListener('storage', upd); };
  }, []);

  const navPrimary: NavItem[] = [
    { label: "sidebar.dashboard", path: "/dashboard", icon: LayoutDashboard },
    { label: "sidebar.company", path: "/company-profile", icon: Building2 },
    { label: "sidebar.profile", path: "/profile", icon: UserRound },
    { label: "sidebar.dataRoom", path: isAccounting ? "/accounting/data-room" : "/data-room", icon: FolderLock },
    { label: "sidebar.articles", path: "/articles", icon: BookOpen },
  ];

  const navAnalytics: NavItem[] = isAccounting
    ? [
        { label: "sidebar.financialHealth", path: "/financial-health-check", icon: Activity },
      ]
    : [
        { label: "sidebar.irScore", path: "/ir-score", icon: Gauge },
        { label: "sidebar.financials", path: "/financials", icon: LineChart },
        { label: "sidebar.kpis", path: "/kpis", icon: BarChart3 },
      ];

  const navServices: NavItem[] = isAccounting
    ? [
        { label: "My Services", path: "/my-services", icon: Briefcase },
        { label: "sidebar.referAndEarn", path: "/refer-and-earn", icon: Gift },
        { label: "sidebar.settings", path: "/settings", icon: Settings },
      ]
    : [
        { label: "sidebar.referAndEarn", path: "/refer-and-earn", icon: Gift },
        { label: "sidebar.settings", path: "/settings", icon: Settings },
      ];

  const handleNavigation = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  /* Nested routes count as the section they came from, so opening a document
     under /data-room/… no longer drops the highlight off the whole nav. */
  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const row = (item: NavItem) => (
    <NavRow
      key={item.label}
      item={item}
      active={isActive(item.path)}
      badge={item.path === '/notifications' ? unread : undefined}
      onNavigate={handleNavigation}
    />
  );

  const defaultLogout = () => {
    logout();
    navigate('/');
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    } else {
      defaultLogout();
    }
  };

  return (
    <aside className="hidden md:flex w-64 bg-white border-e border-gray-200 flex-col fixed h-full start-0">
      {/* Logo renders its own <Link>, so nothing wraps it — the anchor that used
          to sit here nested one link inside another and forced a full page
          reload every time the logo was clicked. */}
      <div className="flex h-[68px] shrink-0 items-center border-b border-gray-100 px-5">
        <Logo className="h-8 w-auto" to="/dashboard" />
      </div>

      <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
        {/* Primary Navigation */}
        <ul className="space-y-0.5">{navPrimary.map(row)}</ul>

        {/* Analytics Section */}
        <div>
          <SectionLabel>Analytics</SectionLabel>
          <ul className="space-y-0.5">{navAnalytics.map(row)}</ul>
        </div>

        {/* Communications Section */}
        <div>
          <SectionLabel>Communications</SectionLabel>
          <ul className="space-y-0.5">{navComms.map(row)}</ul>
        </div>

        {/* Services + account. Sits on the floor of the nav above a divider, so
            the utility rows read as a footer group rather than a fourth list
            that ran out of a heading. */}
        <div className="mt-auto border-t border-gray-100 pt-4">
          <ul className="space-y-0.5">{navServices.map(row)}</ul>
        </div>
      </nav>

      <div className="shrink-0 border-t border-gray-200 p-3">
        <button
          onClick={handleLogoutClick}
          className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-start text-sm text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
          <span>{t('sidebar.signOut')}</span>
        </button>
      </div>
    </aside>
  );
}
